import React, { useState } from 'react';
import { useWBL } from '../../context/WBLContext';
import { Cake, ShieldCheck, CheckCircle2, UserX, UserCheck, Lock, Sparkles, Send, X } from 'lucide-react';

interface Ferpa18TransitionModalProps {
  onClose: () => void;
}

export const Ferpa18TransitionModal: React.FC<Ferpa18TransitionModalProps> = ({ onClose }) => {
  const { activeUser, grantFerpaConsent, updateUserProfile, process18thBirthdayTransition } = useWBL();

  const [signatureName, setSignatureName] = useState(activeUser?.name || '');
  const [certified, setCertified] = useState(false);
  const [allowParentOversight, setAllowParentOversight] = useState<boolean>(
    activeUser?.allowParentOversightAt18 !== false
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedSuccess, setSubmittedSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!certified || !signatureName.trim()) return;

    setIsSubmitting(true);
    try {
      // 1. Grant FERPA Self-Consent Signature
      await grantFerpaConsent(true, {
        type: 'type',
        value: signatureName.trim(),
        name: signatureName.trim(),
        date: new Date().toISOString()
      });

      // 2. Save 18yo Parent Oversight Choice & Transition Status
      if (activeUser?.uid) {
        await process18thBirthdayTransition(activeUser.uid, allowParentOversight);
      }

      setSubmittedSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Failed to process 18th birthday FERPA transition:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const parentEmailDisplay = activeUser?.parentEmail || activeUser?.ferpaConsentRecipientEmail || 'your parent/guardian';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs animate-fade-in overflow-y-auto">
      <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl max-w-xl w-full p-6 sm:p-8 relative animate-scale-up space-y-6 my-8">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-5 right-5 p-2 text-slate-400 hover:text-slate-700 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
          title="Close Modal"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Celebratory Birthday Banner Header */}
        <div className="bg-gradient-to-r from-[#0094c8] via-[#0277a3] to-[#0f2d4a] text-white p-6 rounded-2xl relative overflow-hidden space-y-2">
          <div className="flex items-center space-x-2.5">
            <div className="p-2 bg-white/20 rounded-xl backdrop-blur-xs">
              <Cake className="w-6 h-6 text-amber-300" />
            </div>
            <span className="text-xs font-black uppercase tracking-widest text-amber-200 font-mono">
              Age of Majority Reached (18yo)
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl font-black uppercase tracking-tight">
            Happy 18th Birthday, {activeUser?.contactFirstName || activeUser?.name?.split(' ')[0] || 'Student'}! 🎉
          </h2>
          <p className="text-xs text-sky-100 leading-relaxed font-medium">
            Under federal FERPA law (34 CFR § 99.5), educational privacy rights now transfer directly to you as an adult student.
          </p>
        </div>

        {submittedSuccess ? (
          <div className="p-6 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-2xl text-center space-y-3 animate-fade-in">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
            <h3 className="text-lg font-black uppercase tracking-tight">18yo FERPA Self-Consent Recorded!</h3>
            <p className="text-xs text-emerald-700 font-medium">
              Your self-consent authorization signature and parent oversight settings have been updated.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Step 1: Self-Consent Signature */}
            <div className="space-y-3 p-4 bg-slate-50 border border-slate-200/80 rounded-2xl">
              <div className="flex items-center space-x-2 text-slate-900">
                <ShieldCheck className="w-4 h-4 text-[#0094c8] shrink-0" />
                <h3 className="text-xs font-black uppercase tracking-wider">
                  Step 1 • Student FERPA Self-Consent Release
                </h3>
              </div>

              <p className="text-xs text-slate-600 leading-relaxed font-medium">
                As an 18-year-old adult, your minor parent consent has been reset. Please sign your adult self-consent authorization below to allow pathway advisors and host employers to access your work-based learning records.
              </p>

              <div className="p-3 bg-white border border-slate-200 rounded-xl space-y-2">
                <label className="flex items-start space-x-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={certified}
                    onChange={(e) => setCertified(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 text-[#0094c8] focus:ring-[#0094c8] accent-[#0094c8] shrink-0 cursor-pointer"
                  />
                  <span className="text-[11px] font-semibold text-slate-700 leading-snug select-none">
                    "I certify that I am 18 years of age or older. I hereby self-authorize the Work-Based Learning Portal to release my educational records, attendance logs, and evaluation reports to participating pathway advisors and employer partners."
                  </span>
                </label>
              </div>

              <div className="space-y-1">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">
                  Typed Student Legal Signature
                </label>
                <input
                  type="text"
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                  placeholder="Type your full legal name..."
                  required
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 focus:border-[#0094c8] focus:ring-1 focus:ring-[#0094c8] rounded-xl text-xs font-bold text-slate-800 outline-none transition-all font-mono"
                />
              </div>
            </div>

            {/* Step 2: Parent Oversight Preferences */}
            <div className="space-y-3 p-4 bg-slate-50 border border-slate-200/80 rounded-2xl">
              <div className="flex items-center space-x-2 text-slate-900">
                <Lock className="w-4 h-4 text-emerald-600 shrink-0" />
                <h3 className="text-xs font-black uppercase tracking-wider">
                  Step 2 • Parent Dashboard Oversight Selection
                </h3>
              </div>

              <p className="text-xs text-slate-600 leading-relaxed font-medium">
                Do you wish to allow your parent or guardian (<strong className="text-slate-800">{parentEmailDisplay}</strong>) to continue viewing your student progress and placements on the Parent Dashboard?
              </p>

              <div className="space-y-2">
                <label
                  onClick={() => setAllowParentOversight(true)}
                  className={`p-3 rounded-xl border flex items-center justify-between cursor-pointer transition-all ${
                    allowParentOversight
                      ? 'bg-blue-50/70 border-blue-300 text-blue-950 font-bold shadow-2xs'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100/50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <UserCheck className={`w-4 h-4 ${allowParentOversight ? 'text-[#0094c8]' : 'text-slate-400'}`} />
                    <div className="text-xs">
                      <span className="block font-black uppercase">Yes, Allow Parent Dashboard Oversight</span>
                      <span className="text-[10px] text-slate-500 font-normal">Parent can view applications, attendance, and placements.</span>
                    </div>
                  </div>
                  <input
                    type="radio"
                    name="parentOversight"
                    checked={allowParentOversight}
                    onChange={() => setAllowParentOversight(true)}
                    className="accent-[#0094c8]"
                  />
                </label>

                <label
                  onClick={() => setAllowParentOversight(false)}
                  className={`p-3 rounded-xl border flex items-center justify-between cursor-pointer transition-all ${
                    !allowParentOversight
                      ? 'bg-rose-50/70 border-rose-300 text-rose-950 font-bold shadow-2xs'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100/50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <UserX className={`w-4 h-4 ${!allowParentOversight ? 'text-rose-600' : 'text-slate-400'}`} />
                    <div className="text-xs">
                      <span className="block font-black uppercase">No, Self-Manage Account (Revoke Parent Access)</span>
                      <span className="text-[10px] text-slate-500 font-normal">Parent access to student dashboard will be disabled.</span>
                    </div>
                  </div>
                  <input
                    type="radio"
                    name="parentOversight"
                    checked={!allowParentOversight}
                    onChange={() => setAllowParentOversight(false)}
                    className="accent-rose-600"
                  />
                </label>
              </div>
            </div>

            {/* Action Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || !certified || !signatureName.trim()}
              className="w-full py-3.5 px-6 bg-[#0094c8] hover:bg-[#007ba6] text-white rounded-2xl text-xs font-black uppercase tracking-wider transition-all shadow-md flex items-center justify-center space-x-2 cursor-pointer disabled:opacity-50"
            >
              <Sparkles className="w-4 h-4" />
              <span>Submit 18yo FERPA Self-Consent & Save Oversight Preference</span>
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
